import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Clock, Sparkles } from 'lucide-react';

import { ScreenLayout } from '@/components/shared/ScreenLayout';
import { Button } from '@/components/ui/Button';
import { ProfileSummaryCard } from '@/components/result/ProfileSummaryCard';
import { useSession } from '@/hooks/useSession';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '@/lib/cn';
import logoLight from '@/assets/logo/logo-light.png';
import logoDark from '@/assets/logo/logo-dark.png';

/**
 * Экран 1 — Приветствие.
 *
 * Дизайн phase-2.5 (UA 30.04.2026):
 *  - Заголовок + подзаголовок (что будет за 5 минут).
 *  - ProfileSummaryCard в режиме 'preview' с захардкодженным «Михаил, 9 лет,
 *    8/11, Уверенный, Логика+Поиск, Программирование, Дизайн+Игры». Бейдж
 *    «ПРИМЕР РЕЗУЛЬТАТА» сверху + «после 5 минут теста». Тот же визуал
 *    использует Result top-fold — преемственность гарантирует, что родитель
 *    получит то, что ему обещано.
 *  - 3 info-bullets (👥 Подходит детям 6–14 / 🕐 Занимает 5–7 минут /
 *    ✨ Цифровой профиль) с peach-плашками иконок.
 *  - CTA «Начать тест» fixed bottom + микротекст implied consent
 *    (discrepancies №20: согласие на ПДн фиксируется здесь).
 */

const INFO_BULLETS = [
  {
    Icon: Users,
    title: 'Подходит детям 6–14 лет',
    subtitle: 'Адаптируется под возраст ребёнка',
  },
  {
    Icon: Clock,
    title: 'Занимает 5–7 минут',
    subtitle: 'Можно проходить вместе',
  },
  {
    Icon: Sparkles,
    title: 'Цифровой профиль',
    subtitle: 'Уровень + сильные стороны + направление',
  },
];

export default function Welcome() {
  const navigate = useNavigate();
  const { openModal, recordBothConsents, state } = useSession();
  const { track } = useAnalytics();
  const { theme } = useTheme();

  // Local checkbox state. Default unchecked при mount (если в session
  // ещё нет timestamps). Если оба timestamps уже в state (recovery
  // в той же сессии до city change) — checked.
  const [pdChecked, setPdChecked] = useState(state.consents.dataConsentAt !== null);
  const [marketingChecked, setMarketingChecked] = useState(
    state.consents.marketingConsentAt !== null,
  );

  const canStart = pdChecked && marketingChecked;

  const handleStart = () => {
    if (!canStart) return;
    // Атомарно фиксируем оба timestamps по клику CTA, не по клику
    // checkbox. Юридически чище: момент перехода дальше — единственный
    // factual момент явного согласия. Идемпотентно — если уже set,
    // не перезаписываем (audit trail сохраняет первый клик).
    recordBothConsents();
    track('data_consent_given');
    track('start_test_click');
    navigate('/child-info');
  };

  return (
    <ScreenLayout
      hideHeader
      cta={
        <div className="flex flex-col gap-3">
          {/* Inline ссылки в тексте чекбокса (KIBERone HQ UX).
              Структура: visual checkbox + invisible input в одном
              <label htmlFor>; текст справа — несколько отдельных
              <label htmlFor> для plain fragments + <button> для
              ссылок. Browser forwards label click на input для
              plain text, но buttons sibling label НЕ forwardят
              (click на button открывает modal без toggle).
              aria-label на input даёт полный accessible name для
              screen reader'ов (concat нескольких label'ов теряет
              текст buttons). */}
          {/* Asterisk attached к last word без space (no break opportunity)
              → asterisk не orphan'ится на новую строку. Inline buttons
              остаются clickable; plain-fragment labels через <label htmlFor>
              forward click к input (toggle). */}
          <ConsentCheckbox
            id="consent-pd"
            checked={pdChecked}
            onChange={setPdChecked}
            ariaLabel="Даю согласие на обработку персональных данных в соответствии с политикой конфиденциальности"
          >
            <label htmlFor="consent-pd" className="cursor-pointer">
              Даю{' '}
            </label>
            <button
              type="button"
              onClick={() => openModal('data-consent')}
              className="text-primary underline underline-offset-2 no-tap-highlight"
            >
              согласие на обработку персональных данных
            </button>
            <label htmlFor="consent-pd" className="cursor-pointer">
              {' '}в соответствии с{' '}
            </label>
            <button
              type="button"
              onClick={() => openModal('privacy-policy')}
              className="text-primary underline underline-offset-2 no-tap-highlight"
            >
              политикой конфиденциальности
            </button>
            <label htmlFor="consent-pd" className="cursor-pointer">
              <span aria-hidden="true">*</span>
            </label>
          </ConsentCheckbox>
          <ConsentCheckbox
            id="consent-marketing"
            checked={marketingChecked}
            onChange={setMarketingChecked}
            ariaLabel="Я соглашаюсь получать информационную рассылку"
          >
            <label htmlFor="consent-marketing" className="cursor-pointer">
              Я соглашаюсь получать{' '}
            </label>
            <button
              type="button"
              onClick={() => openModal('mailing-policy')}
              className="text-primary underline underline-offset-2 no-tap-highlight"
            >
              информационную рассылку
            </button>
            <label htmlFor="consent-marketing" className="cursor-pointer">
              <span aria-hidden="true">*</span>
            </label>
          </ConsentCheckbox>
          <Button onClick={handleStart} disabled={!canStart} fullWidth>
            Начать тест
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* Brand-mark на Welcome: полный логотип во всю ширину контентной области. */}
        {/* Container locks aspect-ratio к light Welcome PNG (1774×887 = 2:1).
            Light logo has intrinsic 2:1 aspect и rendering точно fill'ит box.
            Dark logo has 4.96:1 aspect (короче) — без фиксированного aspect-
            container'а dark logo рендерится наполовину ниже → '-mb-12' negative
            margin pulled following text up в overlap зону.

            object-contain + items-center центрирует dark logo внутри light-
            sized блока вертикально, оставляя equal whitespace top+bottom.
            Спейсинг под заголовок остаётся идентичным независимо от темы. */}
        <div className="-mx-5 -mt-12 -mb-12 flex justify-center">
          <div className="w-4/5 aspect-[1774/887] flex items-center justify-center">
            <img
              src={theme === 'dark' ? logoDark : logoLight}
              alt="АйТи-Профиль · диагностика · профориентация · развитие"
              className="block w-full h-full object-contain select-none"
              draggable={false}
            />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <h2 className="text-2xl font-bold leading-tight text-text-primary">
            Узнайте, что у ребёнка получается в IT и какое направление ему ближе
          </h2>
          <p className="text-base leading-relaxed text-text-secondary">
            Бесплатный тест за 5 минут для детей 6–14 лет. Покажем сильные стороны и направление в
            IT.
          </p>
        </div>

        {/* Preview: ровно та же карточка-сводка, что появится на финальном
            Result-экране — гарантия, что родитель получит обещанное. */}
        <ProfileSummaryCard
          mode="preview"
          name="Михаил"
          age={9}
          level="Уверенный"
          score={{ value: 8, max: 11 }}
          strengths={['Безопасность', 'Файлы и папки']}
          topDirection="PROG"
          tryDirections={['DESIGN', 'GAME']}
        />

        {/* 3 info-bullets с peach-иконками */}
        <ul className="flex flex-col gap-2.5 mt-1">
          {INFO_BULLETS.map(({ Icon, title, subtitle }) => (
            <li
              key={title}
              className="flex items-start gap-3 rounded-xl border border-border bg-surface p-3.5"
            >
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                <Icon size={20} strokeWidth={1.75} />
              </span>
              <span className="flex flex-col">
                <span className="text-base font-semibold text-text-primary leading-tight">
                  {title}
                </span>
                <span className="text-sm text-text-secondary mt-0.5">{subtitle}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </ScreenLayout>
  );
}

/**
 * Consent checkbox с inline button links в тексте.
 *
 * Структура (важна для accessibility и no-toggle при клике по ссылке):
 *  - <label htmlFor={id}> обернёт visual square + invisible <input>.
 *    Клик на square тоггл'ит input через browser label-forwarding.
 *  - children: серия <label htmlFor={id}> для plain fragments
 *    (клик → toggle) + <button type="button"> для inline ссылок
 *    (клик → openModal, НЕ toggle, т.к. buttons sibling label).
 *  - aria-label на input — полный текст согласия для screen reader'ов.
 *    Без aria-label AT прочитала бы только concat plain labels без
 *    текстов buttons (важные слова потерялись бы).
 */
function ConsentCheckbox({
  id,
  checked,
  onChange,
  ariaLabel,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    // items-center — для 1-2 line labels (типичный mobile-rendering)
    // checkbox визуально центрируется относительно текст-блока. Для
    // 3+ line edge case (очень узкий экран) checkbox остаётся в
    // вертикальном центре — приемлемо. Альтернатива items-start с
    // mt-0.5 давала «прилипание к верху», что spec явно отверг.
    <div className="flex items-center gap-3 no-tap-highlight">
      <label htmlFor={id} className="cursor-pointer shrink-0">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          aria-label={ariaLabel}
          className="sr-only peer"
        />
        <span
          aria-hidden="true"
          className={cn(
            'inline-flex h-5 w-5 items-center justify-center rounded-md border-2',
            'transition-colors duration-150',
            checked
              ? 'border-accent bg-accent text-white'
              : 'border-border bg-background text-transparent',
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-primary peer-focus-visible:outline-offset-2',
          )}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M2 6.5l2.5 2.5L10 3.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </label>
      {/* min-w-0 разрешает flex-child shrink ниже content size (long
          букв-слова без break-opportunity не вылезают за ширину строки).
          text-[13px] чуть меньше text-sm (14px) → лучше fit'ит первый
          consent-row в 2 строки на mobile width (360-414px). */}
      <div className="min-w-0 flex-1 text-left text-[13px] leading-snug text-text-primary">
        {children}
      </div>
    </div>
  );
}
